<template>
  <v-menu
    v-if="hasAnyItemsInMenu"
    v-model="showingMenu"
    v-bind:close-on-content-click="false"
    location="bottom"
    eager
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }"/>
    </template>

    <v-list data-cy="pod-file-list">
      <v-list-item
        v-if="canShowSaveFrameDialog"
        @click="showSaveFrameDialog"
        data-cy="pod-file-list-saveFrame"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-file-video</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.menuTools.saveFrame') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowSetFrameAsThumbnailDialog"
        @click="showSetFrameAsThumbnailDialog"
        data-cy="pod-file-list-setFrameAsThumbnail"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-video-stabilization</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.menuTools.setFrameAsThumb') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider v-if="isVideoPod" aria-hidden="true" />

      <v-list-item
        v-if="canShowTranscribeSelectedDialog"
        @click="showTranscribeSelectedDialog"
        data-cy="pod-file-list-transcribe"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-transcribe</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('menus.tools.generateSubtitles') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowGenerateAudioDialog"
        @click="showGenerateAudioDialog"
        data-cy="pod-file-list-generateAudio"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-music-note</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.menuGenerateAudio.generateAudio') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowLabelSelectedDialog"
        @click="showLabelSelectedDialog"
        data-cy="pod-file-list-labelAndTextDetect"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-label</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.labelAndTextDetect') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowConvertVideoFileDialog"
        @click="showConvertVideoDialog"
        data-cy="pod-file-list-convertVideo"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-multimedia</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.convertToCustomFormat') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowConvertAudioFileDialog"
        @click="showConvertAudioFileDialog"
        data-cy="pod-file-list-convertAudio"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-multimedia</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.convertToCustomFormat') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowConvertImageFileDialog"
        @click="showConvertImageFileDialog"
        data-cy="pod-file-list-convertImage"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-multimedia</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.convertToCustomFormat') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canShowMediaConverterDialog"
        @click="showMediaConverterDialog"
        data-cy="pod-file-list-convertToSocialMedia"
      >
        <template v-slot:prepend>
          <v-icon size="small">mdi-video-box</v-icon>
        </template>

        <v-list-item-title>
          {{ $t('routes.pod.convertToSocialMedia') }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>

  <SaveFrameDialog
    v-if="canShowSaveFrameDialog"
    v-model="showingSaveFrameDialog"
    :pod="pod"
  />

  <SetFrameAsThumbnailDialog
    v-if="canShowSetFrameAsThumbnailDialog"
    v-model="showingSetFrameAsThumbnailDialog"
    :pod="pod"
  />

  <TranscribeSelectedDialog
    v-if="canShowTranscribeSelectedDialog"
    v-model="showingTranscribeSelectedDialog"
    :pods="[pod]"
    transcriptionContext="podView"
  />

  <LabelSelectedDialog
    v-if="canShowLabelSelectedDialog"
    v-model="showingLabelSelectedDialog"
    :pods="[pod]"
  />

  <ConvertVideoFileDialog
    v-if="canShowConvertVideoFileDialog"
    v-model="showingConvertVideoFileDialog"
    :pods="[pod]"
  />

  <ConvertAudioFileDialog
    v-if="canShowConvertAudioFileDialog"
    v-model="showingConvertAudioFileDialog"
    :pods="[pod]"
  />

  <ConvertImageFileDialog
    v-if="canShowConvertImageFileDialog"
    v-model="showingConvertImageFileDialog"
    :pods="[pod]"
  />

  <ServiceMediaConverterDialog
    v-if="canShowMediaConverterDialog"
    v-model="showingMediaConverterDialog"
    :pod="pod"
  />

  <GenerateAudioDialog
    v-if="canShowGenerateAudioDialog"
    v-model="showingGenerateAudioDialog"
    :pod="pod"
  />
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import SaveFrameDialog from "../dialogs/SaveFrameDialog.vue"
import SetFrameAsThumbnailDialog from "../dialogs/SetFrameAsThumbnailDialog.vue"

import TranscribeSelectedDialog from "../../../nodes2/components/dialogs/TranscribeSelectedDialog.vue"
import GenerateAudioDialog from "../dialogs/GenerateAudioDialog.vue"
import LabelSelectedDialog from "../dialogs/LabelSelectedDialog.vue"

import ConvertVideoFileDialog from "../dialogs/convertFile/ConvertVideoFileDialog.vue"
import ConvertAudioFileDialog from "../dialogs/convertFile/ConvertAudioFileDialog.vue"
import ConvertImageFileDialog from "../dialogs/convertFile/ConvertImageFileDialog.vue"
import ServiceMediaConverterDialog from "../dialogs/ServiceMediaConverterDialog.vue"

export default {
  name: "FileContextMenu",
  components: {
    SaveFrameDialog,
    SetFrameAsThumbnailDialog,

    TranscribeSelectedDialog,
    GenerateAudioDialog,
    LabelSelectedDialog,

    ConvertVideoFileDialog,
    ConvertAudioFileDialog,
    ConvertImageFileDialog,
    ServiceMediaConverterDialog,
  },
  data() {
    return {
      showingMenu: false,
      showingSaveFrameDialog: false,
      showingSetFrameAsThumbnailDialog: false,
      showingTranscribeSelectedDialog: false,
      showingGenerateAudioDialog: false,
      showingLabelSelectedDialog: false,
      showingConvertVideoFileDialog: false,
      showingConvertAudioFileDialog: false,
      showingConvertImageFileDialog: false,
      showingMediaConverterDialog: false,
    }
  },
  methods: {
    stub: function() {
      this.showingMenu = false;
    },
    showSetFrameAsThumbnailDialog: function() {
      this.showingSetFrameAsThumbnailDialog = true;
      this.showingMenu = false;
    },
    showSaveFrameDialog: function() {
      this.showingSaveFrameDialog = true;
      this.showingMenu = false;
    },
    showTranscribeSelectedDialog: function() {
      this.showingTranscribeSelectedDialog = true;
      this.showingMenu = false
    },
    showGenerateAudioDialog: function() {
      this.showingGenerateAudioDialog = true;
      this.showingMenu = false;
    },
    showLabelSelectedDialog: function() {
      this.showingLabelSelectedDialog = true;
      this.showingMenu = false;
    },
    showConvertVideoDialog: function() {
      this.showingConvertVideoFileDialog = true;
      this.showingMenu = false;
    },
    showConvertAudioFileDialog: function() {
      this.showingConvertAudioFileDialog = true;
      this.showingMenu = false;
    },
    showConvertImageFileDialog: function() {
      this.showingConvertImageFileDialog = true;
      this.showingMenu = false;
    },
    showMediaConverterDialog: function() {
      this.showingMediaConverterDialog = true;
      this.showingMenu = false;
    },
  },
  computed: {
    hasAnyItemsInMenu: function() {
      return (this.isSuitableForService || this.isImagePod)
      && ((this.$canRunFreeService()
        || this.$canRunPaidService())
        || (this.$canListPodService() && this.$canControlPodService()
      ))
    },
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    isImagePod: function() {
      const podStore = usePodStore();
      return podStore.isImagePod;
    },
    isAudioAvailable: function() {
      if (this.pod && this.isAudioPod) {
        if (Array.isArray(this.pod?.asset?.web) && this.pod.asset.web.length > 0) {
          return true;
        }
      }

      if (this.pod && this.isVideoPod) {
        if (Array.isArray(this.pod?.asset?.audio) && this.pod.asset.audio.length > 0) {
          return true;
        }
      }

      return false;
    },
    isSuitableForService: function() {
      if (this.pod) {
        if (this.isVideoPod) {
          if (Array.isArray(this.pod.asset.web) && this.pod.asset.web.length > 0) {
            return true;
          }

          if (Array.isArray(this.pod.asset.audio) && this.pod.asset.audio.length > 0) {
            return true;
          }
        }

        if (this.isAudioPod) {
          if (Array.isArray(this.pod?.asset?.web) && this.pod.asset.web.length > 0) {
            return true;
          }
        }
      }

      return false;
    },
    canGenerateAudio: function() {
      return this.isAudioPod || (this.pod.asset
        && this.pod.asset.raw
        && this.pod.asset.raw[0].audiocodec !== null
        && this.pod.asset.raw[0].audiobitrate > 0
        && this.pod.asset.raw[0].audiochannels !== null
      );
    },
    canShowGenerateAudioDialog: function() {
      return this.pod && this.$canRunPaidService() &&
        (this.isVideoPod || this.isAudioPod) && !this.isAudioAvailable && this.canGenerateAudio;
    },
    canShowSaveFrameDialog: function() {
      return this.$canRunFreeService() && this.isVideoPod;
    },
    canShowSetFrameAsThumbnailDialog: function() {
      return this.$canRunFreeService() && this.isVideoPod;
    },
    canShowTranscribeSelectedDialog: function() {
      return this.pod && this.$canRunPaidService() && (this.isVideoPod || this.isAudioPod) && this.isAudioAvailable;
    },
    canShowLabelSelectedDialog: function() {
      return this.$canRunPaidService() && this.$canLabelPods() && (this.isVideoPod || this.isImagePod);
    },
    canShowConvertVideoFileDialog: function() {
      return this.pod && this.$canRunPaidService() && this.isVideoPod;
    },
    canShowConvertAudioFileDialog: function() {
      return this.pod && this.$canRunPaidService() && this.isAudioPod;
    },
    canShowConvertImageFileDialog: function() {
      return this.pod && this.$canRunPaidService() && this.isImagePod;
    },
    canShowMediaConverterDialog: function() {
      return this.pod && this.$canRunPaidService() && this.isVideoPod;
    }
  },
}
</script>
